import { addDays, addWeeks, addMonths, addYears, getDaysInMonth, setDate, format, parseISO } from 'date-fns'
import type { RepeatPattern, Task } from './types'

// Normalize a weekDays array to the valid 0-6 (Sun-Sat) domain, sorted ascending.
function normalizeWeekDays(weekDays?: number[]): number[] {
  return (weekDays ?? [])
    .filter((d) => Number.isInteger(d) && d >= 0 && d <= 6)
    .sort((a, b) => a - b)
}

// Advance by `months` while anchoring to the originally intended day-of-month,
// so a task due on the 31st (or Feb 29) does not permanently drift to the 28th.
function advanceByMonths(base: Date, months: number, intendedDay: number): Date {
  const advanced = addMonths(base, months) // clamps; we recompute the day below
  return setDate(advanced, Math.min(intendedDay, getDaysInMonth(advanced)))
}

// Advance by `years` while anchoring to the intended day-of-month (handles Feb 29).
function advanceByYears(base: Date, years: number, intendedDay: number): Date {
  const advanced = addYears(base, years)
  return setDate(advanced, Math.min(intendedDay, getDaysInMonth(advanced)))
}

// Step forward day-by-day from `base` to the next date whose weekday is in
// `weekDays` (assumed non-empty, normalized). Always lands within 7 days.
function nextSelectedWeekday(base: Date, weekDays: number[]): Date {
  for (let step = 1; step <= 7; step++) {
    const candidate = addDays(base, step)
    if (weekDays.includes(candidate.getDay())) return candidate
  }
  return addDays(base, 7) // unreachable for a non-empty set, kept for safety
}

// Resolve a valid 1–31 day-of-month, falling back to the base date's day when
// pattern.monthDay is absent or out of range (guards against setDate overflow / NaN).
function resolveMonthDay(monthDay: number | undefined, base: Date): number {
  if (typeof monthDay === 'number' && Number.isInteger(monthDay) && monthDay >= 1 && monthDay <= 31) {
    return monthDay
  }
  return base.getDate()
}

export function getNextOccurrence(dueDate: string, pattern: RepeatPattern): string | null {
  if (pattern.type === 'none') return null

  const date = parseISO(dueDate)
  const interval = pattern.interval || 1

  // 선택된 요일 집합으로 다음 발생일을 찾는다 (daily/weekly 공통).
  // weekDays는 데이터 모델상 daily 타입에만 저장되므로 daily 케이스에서 반드시 처리해야 한다.
  const weekDaysSet = normalizeWeekDays(pattern.weekDays)

  let next: Date
  switch (pattern.type) {
    case 'daily':
      if (weekDaysSet.length > 0) {
        // 요일 선택 반복: date 다음으로 선택 요일에 해당하는 가장 가까운 날 (주 경계 넘어 탐색)
        next = nextSelectedWeekday(date, weekDaysSet)
      } else {
        next = addDays(date, interval)
      }
      break
    case 'weekly': {
      if (weekDaysSet.length > 0) {
        const currentDow = date.getDay()
        // Next selected weekday remaining in the current week.
        const nextThisWeek = weekDaysSet.find((d) => d > currentDow)
        if (nextThisWeek !== undefined) {
          next = addDays(date, nextThisWeek - currentDow)
        } else {
          // Wrap to the first selected weekday in the next eligible week,
          // honoring interval at the week boundary.
          const daysToWeekStart = 7 - currentDow // to next Sunday (dow 0)
          const intervalOffset = (interval - 1) * 7 // extra weeks for interval > 1
          next = addDays(date, daysToWeekStart + intervalOffset + weekDaysSet[0])
        }
      } else {
        next = addWeeks(date, interval)
      }
      break
    }
    case 'monthly':
      next = advanceByMonths(date, interval, resolveMonthDay(pattern.monthDay, date))
      break
    case 'yearly':
      next = advanceByYears(date, interval, resolveMonthDay(pattern.monthDay, date))
      break
    default:
      return null
  }

  if (pattern.endDate && format(next, 'yyyy-MM-dd') > pattern.endDate) {
    return null
  }

  return format(next, 'yyyy-MM-dd')
}

export function generateNextTask(task: Task): Partial<Task> | null {
  if (task.repeat.type === 'none' || !task.dueDate) return null

  const nextDate = getNextOccurrence(task.dueDate, task.repeat)
  if (!nextDate) return null

  return {
    title: task.title,
    categoryId: task.categoryId,
    status: 'pending',
    priority: task.priority,
    isFlagged: task.isFlagged,
    isStarred: task.isStarred,
    dueDate: nextDate,
    dueTime: task.dueTime,
    alarm: task.alarm ? { ...task.alarm } : { enabled: false },
    repeat: { ...task.repeat },
    memo: task.memo,
    subtasks: task.subtasks.map(st => ({ ...st, isCompleted: false })),
  }
}

const WEEKDAY_LABELS = ['일', '월', '화', '수', '목', '금', '토']

// Render a comma-joined Korean weekday label list, guarding against
// out-of-range / non-integer indices (e.g. an import using 1-7 instead of 0-6).
function labelForDays(days?: number[]): string {
  return (days ?? [])
    .filter((d) => Number.isInteger(d) && d >= 0 && d <= 6)
    .map((d) => WEEKDAY_LABELS[d])
    .join(', ')
}

export function getRepeatLabel(pattern: RepeatPattern): string {
  if (pattern.type === 'none') return '아니요'

  const interval = pattern.interval || 1
  switch (pattern.type) {
    case 'daily': {
      const base = interval === 1 ? '매일' : `${interval}일마다`
      const dayLabels = labelForDays(pattern.weekDays)
      return dayLabels ? `매주 ${dayLabels}` : base
    }
    case 'weekly': {
      const base = interval === 1 ? '매주' : `${interval}주마다`
      const dayLabels = labelForDays(pattern.weekDays)
      return dayLabels ? `${base} ${dayLabels}` : base
    }
    case 'monthly':
      return interval === 1 ? '매월' : `${interval}개월마다`
    case 'yearly':
      return interval === 1 ? '매년' : `${interval}년마다`
    default:
      return '아니요'
  }
}
